import {
  DataSyncHealthStatus,
  DataSyncMetricsBucket,
  DataSyncMetricsSummary,
} from '../types/DataSyncMetrics';

const toNumber = (value?: number | string): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const toPercentage = (value: number): number => Math.max(0, Math.min(100, value));

const getHealthStatus = (errorRate: number): DataSyncHealthStatus => {
  if (errorRate <= 0) {
    return DataSyncHealthStatus.HEALTHY;
  }

  if (errorRate <= 10) {
    return DataSyncHealthStatus.WARNING;
  }

  return DataSyncHealthStatus.FAILING;
};

export const computeMetricsSummaries = (
  buckets: DataSyncMetricsBucket[]
): Map<string, DataSyncMetricsSummary> => {
  const groupedByDataSync = new Map<string, DataSyncMetricsBucket[]>();

  for (const bucket of buckets) {
    const dataSyncId = bucket.datasync_id;
    if (!dataSyncId) {
      continue;
    }

    const entries = groupedByDataSync.get(dataSyncId) || [];
    entries.push(bucket);
    groupedByDataSync.set(dataSyncId, entries);
  }

  const summaries = new Map<string, DataSyncMetricsSummary>();

  groupedByDataSync.forEach((entries, datasyncId) => {
    const bucketTotals = new Map<string, { success: number; errors: number }>();

    for (const entry of entries) {
      const key = entry.bucket || '';
      const totals = bucketTotals.get(key) || { success: 0, errors: 0 };
      totals.success += toNumber(entry.total_success_count);
      totals.errors += toNumber(entry.total_error_count);
      bucketTotals.set(key, totals);
    }

    const sortedBuckets = Array.from(bucketTotals.entries()).sort((a, b) =>
      new Date(a[0]).getTime() - new Date(b[0]).getTime()
    );

    const sparklineData = sortedBuckets.map(([, totals]) => totals.success);
    const totalSuccess = sortedBuckets.reduce((sum, [, totals]) => sum + totals.success, 0);
    const totalErrors = sortedBuckets.reduce((sum, [, totals]) => sum + totals.errors, 0);
    const totalEvents = totalSuccess + totalErrors;

    const successRate = totalEvents > 0 ? toPercentage((totalSuccess / totalEvents) * 100) : 0;
    const errorRate = totalEvents > 0 ? toPercentage((totalErrors / totalEvents) * 100) : 0;

    summaries.set(datasyncId, {
      datasyncId,
      totalEvents,
      totalErrors,
      successRate,
      errorRate,
      sparklineData,
      healthStatus: getHealthStatus(errorRate),
    });
  });

  return summaries;
};
